import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Fade,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InboxIcon from "@mui/icons-material/Inbox";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RefreshIcon from "@mui/icons-material/Refresh";

import { GlobalSearchBar } from "../Components/ERPList";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useTheme } from "../hooks/useTheme";
import { createMuiThemeFromMode } from "../theme/theme";
import { schedulingApi } from "../Components/schedulingApi";

import styles from "./AllEnquiries.module.scss";

const PAGE_SIZE_OPTIONS = [10, 12, 25, 50, 100];

const APPROVAL_TYPE_LABEL = {
  session_approval: "Session approval",
  reschedule: "Reschedule request",
};

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

export default function ApprovalsPage() {
  const { themeMode } = useTheme();
  const baseThemeOptions = useMemo(
    () => ({
      palette: {
        secondary: { main: "#f5365c" },
        success: { main: "#2dce89" },
        warning: { main: "#fb6340" },
        info: { main: "#11cdef" },
      },
      typography: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        h4: { fontWeight: 600, letterSpacing: "-0.02em" },
        button: { textTransform: "none", fontWeight: 500 },
      },
      shape: { borderRadius: 12 },
      components: {
        MuiButton: { styleOverrides: { root: { boxShadow: "none" } } },
        MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      },
    }),
    []
  );
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode, baseThemeOptions]
  );
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedGlobalSearch = useDebouncedValue(globalSearch, 400);
  const [filterOpen, setFilterOpen] = useState(!isMobile);
  const [filterType, setFilterType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const loadApprovals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventsData, rescheduleData] = await Promise.all([
        schedulingApi.listPendingApprovalEvents(),
        schedulingApi.listRescheduleRequests({ status: "pending" }),
      ]);

      const list = [];
      const events = Array.isArray(eventsData?.events) ? eventsData.events : eventsData || [];
      events.forEach((ev) => {
        list.push({
          id: `session-${ev.id}`,
          type: "session_approval",
          title: ev.title || `Session #${ev.id}`,
          status: "pending_approval",
          date: ev.scheduled?.[0]?.start_datetime || null,
          link: "/schedule/approvals",
          raw: ev,
        });
      });

      const requests = Array.isArray(rescheduleData?.requests) ? rescheduleData.requests : rescheduleData || [];
      requests.forEach((req) => {
        list.push({
          id: `reschedule-${req.id}`,
          type: "reschedule",
          title: req.event_title ? `Reschedule: ${req.event_title}` : `Reschedule request #${req.id}`,
          status: req.status || "pending",
          date: req.requested_start_datetime || null,
          link: "/schedule/reschedule-requests",
          raw: req,
        });
      });

      setItems(list);
      if (list.length === 0 && (eventsData?.error || rescheduleData?.error)) {
        setError(eventsData?.error || rescheduleData?.error || "Failed to load approvals");
      } else {
        setError(null);
      }
    } catch (e) {
      setError(e?.message || "Failed to load approvals");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  const filteredBySearch = useMemo(() => {
    const q = (debouncedGlobalSearch || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => {
      const title = (i.title || "").toLowerCase();
      const status = (i.status || "").toLowerCase();
      const typeLabel = (APPROVAL_TYPE_LABEL[i.type] || i.type || "").toLowerCase();
      return title.includes(q) || status.includes(q) || typeLabel.includes(q);
    });
  }, [items, debouncedGlobalSearch]);

  const filteredByType = useMemo(() => {
    if (!filterType) return filteredBySearch;
    return filteredBySearch.filter((i) => i.type === filterType);
  }, [filteredBySearch, filterType]);

  const totalCount = filteredByType.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const page = Math.min(currentPage, totalPages);
  const displayData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredByType.slice(start, start + pageSize);
  }, [filteredByType, page, pageSize]);

  const resetFilters = () => {
    setGlobalSearch("");
    setFilterType("");
    setCurrentPage(1);
  };

  const handleReview = (item) => {
    if (item.link) navigate(item.link);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
          <Box sx={{ marginBottom: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box>
                <Typography variant="h4" className={styles.pageTitle}>
                  Approvals
                </Typography>
                <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                  Review and approve pending sessions and reschedule requests.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box className={styles.header}>
            <GlobalSearchBar
              value={globalSearch}
              onChange={setGlobalSearch}
              placeholder="Search by title, type, status…"
            />
            <Box className={styles.headerActions}>
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => setFilterOpen(!filterOpen)}
                variant="outlined"
                size="small"
              >
                {filterOpen ? "Hide Filters" : "Show Filters"}
              </Button>
              <Button
                startIcon={<RefreshIcon />}
                onClick={loadApprovals}
                disabled={loading}
                variant="outlined"
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {filterOpen ? (
            <Fade in={filterOpen}>
              <FilterPaper variant="outlined">
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filterType}
                      label="Type"
                      onChange={(e) => {
                        setFilterType(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <MenuItem value="">All types</MenuItem>
                      {Object.entries(APPROVAL_TYPE_LABEL).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>
                    Reset
                  </Button>
                </Stack>
              </FilterPaper>
            </Fade>
          ) : null}

          {loading && (
            <Box className={styles.loadingContainer}>
              <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={400} />
            </Box>
          )}

          {error && !loading && (
            <Typography color="error" sx={{ py: 2 }}>
              {error}
            </Typography>
          )}

          {!loading && filteredByType.length === 0 && (
            <Box className={styles.emptyState}>
              <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No pending approvals
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {items.length === 0 ? "No sessions or reschedule requests require approval." : "Try adjusting your filters or search."}
              </Typography>
            </Box>
          )}

          {!loading && displayData.length > 0 && (
            <>
              <Box className={styles.pagination}>
                <Typography variant="body2" color="text.secondary">
                  Showing {totalCount === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount} results
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Per page</InputLabel>
                    <Select
                      value={pageSize}
                      label="Per page"
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, p) => setCurrentPage(p)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>

              <Paper variant="outlined" className={styles.tableContainer}>
                <Table stickyHeader size="small" className={styles.enquiriesTable}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayData.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        className={styles.tableRow}
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleReview(item)}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {item.type === "session_approval" ? (
                              <EventIcon fontSize="small" />
                            ) : (
                              <ScheduleIcon fontSize="small" />
                            )}
                            <Typography variant="body2">
                              {APPROVAL_TYPE_LABEL[item.type] || item.type}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {item.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.status || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(item.date)}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<OpenInNewIcon />}
                            onClick={() => handleReview(item)}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              <Box className={styles.pagination}>
                <Typography variant="body2" color="text.secondary">
                  Showing {totalCount === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount} results
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Per page</InputLabel>
                    <Select
                      value={pageSize}
                      label="Per page"
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, p) => setCurrentPage(p)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
}
